import React, { useState } from 'react';
import axios from 'axios';
import { CopyIcon, CheckIcon, NfcIcon } from './Icons';

const nfcSupported = typeof window !== 'undefined' && 'NDEFReader' in window;

function SubdomainSection({ user, onUpdateUser }) {
  const [subdomain, setSubdomain] = useState(user?.subdomain || '');
  const [isEditing, setIsEditing] = useState(!user?.subdomain);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkActive, setLinkActive] = useState(user?.link_active !== false);
  const [toggling, setToggling] = useState(false);
  const [nfcState, setNfcState] = useState(null); // null | 'writing' | 'success' | 'error'
  const [nfcMessage, setNfcMessage] = useState('');

  const displayUrl = user?.subdomain ? `${user.subdomain}.jjm.app` : null;
  const fullUrl = displayUrl ? `https://${displayUrl}` : null;

  const handleSaveSubdomain = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/user/subdomain', {
        subdomain: subdomain.toLowerCase()
      });

      onUpdateUser({ ...user, subdomain: response.data.subdomain });
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save subdomain');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLink = async () => {
    setToggling(true);
    const newActiveState = !linkActive;

    try {
      await axios.post('/api/user/toggle-link', {
        active: newActiveState
      });

      setLinkActive(newActiveState);
      onUpdateUser({ ...user, link_active: newActiveState });
    } catch (err) {
      setError('Failed to update link status');
    } finally {
      setToggling(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWriteNfc = async () => {
    setNfcState('writing');
    setNfcMessage('Hold a blank NFC tag against the back of your phone…');

    try {
      const writer = new window.NDEFReader();
      await writer.write({
        records: [{ recordType: 'url', data: fullUrl }]
      });
      setNfcState('success');
      setNfcMessage('Link written. Tapping this tag now opens your contact card.');
      setTimeout(() => setNfcState(null), 5000);
    } catch (err) {
      setNfcState('error');
      if (err.name === 'NotAllowedError') {
        setNfcMessage('NFC permission was denied. Allow NFC access and try again.');
      } else if (err.name === 'AbortError') {
        setNfcMessage('Writing was cancelled.');
      } else {
        setNfcMessage('Could not write to the tag. Make sure it is writable and try again.');
      }
      setTimeout(() => setNfcState(null), 6000);
    }
  };

  return (
    <section className="section">
      <div className="section-header">
        <h2 className="section-title">Your link</h2>
        {user?.subdomain && !isEditing && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setIsEditing(true)}
          >
            Change
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          <div className="form-group">
            <label className="form-label" htmlFor="subdomain-input">Choose your handle</label>
            <div className="subdomain-input-row">
              <input
                id="subdomain-input"
                type="text"
                className="form-input"
                placeholder="yourname"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                autoComplete="off"
                spellCheck="false"
              />
              <span className="subdomain-suffix">.jjm.app</span>
            </div>
            <div className="form-hint">
              Lowercase letters, numbers, and hyphens only
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleSaveSubdomain}
              disabled={loading || !subdomain}
            >
              {loading ? 'Saving…' : 'Save'}
            </button>
            {user?.subdomain && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setIsEditing(false);
                  setSubdomain(user.subdomain);
                  setError('');
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : fullUrl ? (
        <>
          <p className="section-description">
            Anyone who opens this link gets your live contact card.
          </p>

          {error && <div className="error-message">{error}</div>}

          <div className="link-display">
            <span className="link-url">{displayUrl}</span>
            <div className="link-actions">
              <button className="btn btn-outline btn-sm" onClick={handleCopyLink}>
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied ? <span className="copied-note">Copied</span> : 'Copy'}
              </button>
              {nfcSupported && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleWriteNfc}
                  disabled={nfcState === 'writing'}
                >
                  <NfcIcon />
                  {nfcState === 'writing' ? 'Waiting for tag…' : 'Write to NFC tag'}
                </button>
              )}
            </div>
          </div>

          {nfcState && (
            <div className={`nfc-status ${nfcState === 'success' ? 'success' : nfcState === 'error' ? 'error' : ''}`}>
              {nfcMessage}
            </div>
          )}

          <div className="link-toggle-row">
            <div>
              <div className="link-toggle-label">Link enabled</div>
              <div className="link-toggle-hint">
                {linkActive
                  ? 'Visitors receive your contact card'
                  : 'Visitors are redirected away — no contact is shared'}
              </div>
            </div>
            <button
              role="switch"
              aria-checked={linkActive}
              aria-label="Link enabled"
              className={`switch ${linkActive ? 'on' : ''}`}
              onClick={handleToggleLink}
              disabled={toggling}
            />
          </div>
        </>
      ) : (
        <p className="section-description">
          Set up your handle to get your shareable link.
        </p>
      )}
    </section>
  );
}

export default SubdomainSection;
